import { MeetingSessionOvenMediaReceiverManagerBaseData, useOvenMediaVideoSubscriptions } from "@kalyzee/kast-web-sdk";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { useEffect, useMemo } from "react";
import MeetingSessionMediaPlayer, { MeetingSessionMediaPlayerProps } from "../../../../components/player/MeetingSessionMediaPlayer";
import { useFocusedMeetingAPI, useMeetingSelector } from "../../../../hooks/meeting";
import { useStudentContext } from "../context";

import { PedagoPopup, PlayerContainer, ReactIcons, Text } from "@kalyzee/escaet-ui";

import { Meeting, MeetingSessionHighlightingState } from "@kalyzee/kast-web-sdk/websocket";
import { MeetingPlayerTriLayoutContainer } from "../../../../components/player/container/MeetingPlayerTriLayoutContainer";
import { PlayerItem } from "../../../../components/player/interfaces";
import { FullScreenRenderer } from "../../../../components/renderers/FullScreenRenderer";
import { FullScreenButton } from "../../../../components/utils/FullScreenButton";
import { expertWebcamName } from "../../../../helpers/webcam";
import "./dashboard.css";
import styles from "./dashboard.module.css";

const Player = ({
  meeting,
  session,
  style,
  playerProps,
  fullScreen,
  setFullscreen,
}: {
  meeting: Meeting;
  session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>;
  style?: React.CSSProperties;
  playerProps?: MeetingSessionMediaPlayerProps;
  fullScreen?: boolean;
  setFullscreen?: (value: boolean) => void;
}) => {
  const renderPlayerHeader = (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>, focusMode: boolean) => {
    const meetingSession = session.data.session;
    const isPedagogy = meetingSession.extra?.isPedagogy;
    const username = meetingSession.user?.username ?? "";
    const label = session.data.mediaSession.extra?.label ?? username;
    let text = label;
    if (isPedagogy) {
      text = `${label} (Pédagogie)`;
    } else if (meetingSession?.highlighting === MeetingSessionHighlightingState.ONGOING) {
      text = `${label} (Au tableau)`;
    }
    const renderUsername = () => (
      <Text
        style={{
          fontWeight: 800,
          fontSize: focusMode ? "1.2em" : "0.8em",
          backgroundColor: isPedagogy ? "#f9af5c" : "white",
          borderRadius: "0.25em",
          padding: "0.25em 0.5em",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        value={text}
      />
    );

    const renderFullScreenButton = () => <FullScreenButton enabled={!!fullScreen} setEnabled={setFullscreen} />;

    if (focusMode) {
      return (
        <div className={styles.playerHeaderContainer} style={{ backgroundColor: "white", padding: "0.3em" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "1em",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                color: "white",
                fontWeight: 800,
                backgroundColor: "#05C496",
                padding: "0.5em",
                borderRadius: "0.25em",
              }}
              value={"Mode focus".toUpperCase()}
            />
            {renderUsername()}
          </div>
          {renderFullScreenButton()}
        </div>
      );
    }

    return (
      <div className={styles.playerHeaderContainer}>
        {renderUsername()}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "1em",
          }}
        >
          {renderFullScreenButton()}
        </div>
      </div>
    );
  };

  return (
    <PlayerContainer.Default
      containerStyle={{
        backgroundColor: "#9E9D9B",
        overflow: "hidden",
        borderRadius: "0.5em",
        ...(style ?? {}),
      }}
      key={`ovenmedia-session-${session.getId()}-player-container`}
      overlay={"hover"}
      headerContent={renderPlayerHeader(session, false)}
    >
      <MeetingSessionMediaPlayer
        style={{ width: "100%", height: "100%", maxHeight: "100%" }}
        {...(playerProps ?? {})}
        meeting={meeting}
        sessionId={session.data.session.id}
        mediaSessionId={session.data.mediaSession?.id!}
        ovenMediaSession={session}
        muted={true}
        zoomPanPinch={fullScreen}
        callbacks={{
          playingSound: (v) => {
            playerProps?.callbacks?.playingSound?.(v);
          },
        }}
      />
    </PlayerContainer.Default>
  );
};

export const MeetingDashboardStudentPage = () => {
  const studentCtx = useStudentContext();

  const meetingCtx = useFocusedMeetingAPI();
  const [meeting] = useMeetingSelector(meetingCtx, (state) => state.meeting);
  const subscribptionsAPI = useOvenMediaVideoSubscriptions();

  const view = useMemo(() => {
    if (!studentCtx) return undefined;
    if (studentCtx.currentView?.type !== "dashboard_video_only" && studentCtx.currentView?.type !== "normal") {
      const dashboardView = studentCtx.availableViews?.find((v) => v.type === "dashboard_video_only");
      const normalView = studentCtx.availableViews?.find((v) => v.type === "normal");
      return normalView ?? dashboardView ?? studentCtx.currentView;
    }
    return studentCtx.currentView;
  }, [studentCtx]);

  const expertNonWebcamVideoSessions = useMemo(
    () =>
      (view?.experts ?? [])
        .flatMap((data) => data.session.videoSessions)
        .filter((videoSession) => videoSession.data.mediaSession.name !== expertWebcamName),
    [view]
  );

  // Enabled : enabled videos in the left drawer
  const { enabledVideoSessions } = useMemo(() => {
    if (!studentCtx || !meeting) return { enabledVideoSessions: [] };
    const studentMeetingOptions = studentCtx.getMeetingOptions(meeting.id);

    let enabledVideoSessions = expertNonWebcamVideoSessions;

    const enabledVideoSessionsIds = studentMeetingOptions?.enabledVideoSessionIds;
    if (enabledVideoSessionsIds?.length) {
      enabledVideoSessions = enabledVideoSessions.filter((s) => enabledVideoSessionsIds.includes(s.data.mediaSession.id));
    }

    return { enabledVideoSessions };
  }, [expertNonWebcamVideoSessions, studentCtx, meetingCtx]);

  const playerItems = useMemo(
    () =>
      enabledVideoSessions.map(
        (session) =>
          new PlayerItem({
            type: "video_session",
            videoSession: session,
          })
      ),
    [enabledVideoSessions]
  );

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    subscribptionsAPI.update(enabledVideoSessions.map((s) => ({ session: s, active: true })));
    return () => subscribptionsAPI.update(enabledVideoSessions.map((s) => ({ session: s, active: false })));
  }, [subscribptionsAPI, enabledVideoSessions]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!studentCtx) return null;
  if (!meeting) return null;

  const { layout: layoutIndex } = studentCtx;

  const renderNewLayoutContent = () => (
    <MeetingPlayerTriLayoutContainer
      meeting={meeting}
      items={playerItems}
      layoutProps={{ square: { noPages: true } }}
      renderItem={(rendererProvider, item) => {
        const itemData = item.getData();
        if (itemData.type !== "video_session") return rendererProvider(); // should not happen
        return (
          <FullScreenRenderer
            render={(enabled, setEnabled) => (
              <Player meeting={meeting} session={itemData.videoSession} fullScreen={enabled} setFullscreen={(val) => setEnabled(val)} />
            )}
          />
        );
      }}
      layout={layoutIndex}
      autoOnOffVideo={false}
    />
  );

  if (studentCtx?.activityData?.mode === "pedagogy") {
    return (
      <PedagoPopup.Message
        title={"Intervention en cours..."}
        severity={"info"}
        icon={<ReactIcons.Clock style={{ width: "100%", height: "100%" }} />}
        mainMessage="Le cours va reprendre après l'intervention de la pédagogie."
        secondaryMessage="Merci de patienter."
        containerStyle={{
          background: "#00000000",
          zIndex: 0,
        }}
      />
    );
  }

  return (
    <div className={styles.container}>
      {renderNewLayoutContent()}
      {/* {renderFocusContent()} */}
    </div>
  );
};
